import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import "../styles/globalCss.css";

// جداکننده سه‌رقمی برای نمایش قیمت‌ها
const formatWithCommas = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num =
    typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US");
};

// حذف جداکننده و تبدیل به عدد خام برای ذخیره در state
// اگر ورودی نامعتبر باشد (مثل "12.3.4") به‌جای NaN صفر برمی‌گرداند
// تا مقدار نامعتبر هرگز به سرور ارسال نشود.
const parseCommaNumber = (str) => {
  const cleaned = String(str).replace(/[^\d.-]/g, "");
  if (cleaned === "") return 0;
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
};

// روزهای هفته به ترتیب مطابق Date.getDay() جاوااسکریپت (0 = یکشنبه)
const DAY_NAMES_EN = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DAY_LABELS_FA = {
  Sunday: "یکشنبه",
  Monday: "دوشنبه",
  Tuesday: "سه‌شنبه",
  Wednesday: "چهارشنبه",
  Thursday: "پنجشنبه",
  Friday: "جمعه",
  Saturday: "شنبه",
};

const getTodayDayOfWeek = () => DAY_NAMES_EN[new Date().getDay()];

// "14:00" یا "14:00:00" -> "14:00" برای <input type="time">
const toHHMM = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5);
};

// استخراج پیام خطای سرور در صورت وجود، در غیر این صورت پیام پیش‌فرض
const extractErrorMessage = (err, fallback) =>
  err?.response?.data?.message || fallback;

// درخواست GET با هدرهای ضدکش، به‌علاوه یک پارامتر یکتا در URL، تا هیچ
// لایه‌ای (کش مرورگر، پراکسی و ...) پاسخ قدیمی را به‌جای درخواست تازه
// برنگرداند. این تضمین می‌کند مقداری که پس از بازگشت به صفحه دیده می‌شود
// همیشه واقعاً از سرور خوانده شده، نه یک نسخه‌ی کش‌شده.
const getFresh = (url) => {
  const sep = url.includes("?") ? "&" : "?";
  return axios.get(`${url}${sep}_=${Date.now()}`, {
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  });
};

function Markets() {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const todayDayOfWeek = getTodayDayOfWeek();

  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("markets");
  const [savingAsset, setSavingAsset] = useState(null);

  // فیلدهای در حال ویرایش دستی قیمت (برای جلوگیری از پرش نشانگر هنگام تایپ)
  const [editingField, setEditingField] = useState(null); // `${asset}-${field}` یا null
  const [draftValues, setDraftValues] = useState({}); // `${asset}-${field}` -> string

  // ---------------------------------------------------------------------
  // منبع واحد حقیقت برای ساعات کاری/وضعیت هر روز.
  //
  // به‌جای دو state جداگانه (یکی برای «وضعیت امروز» و یکی برای «ساعات کاری
  // روز انتخاب‌شده») که می‌توانستند برای یک روز یکسان، دو نسخه‌ی متفاوت و
  // ناهم‌خوان از داده نگه دارند، اینجا فقط یک cache وجود دارد که با نام روز
  // ایندکس می‌شود: dayData["Sunday"] = { isOpen, buyIsOpen, sellIsOpen,
  // openingTime, closingTime }. هم کارت وضعیت امروز (تب مدیریت بازارها) و
  // هم فرم ساعات کاری (تب ساعات کاری بازار) از همین یک منبع می‌خوانند.
  // ---------------------------------------------------------------------
  const [dayData, setDayData] = useState({});
  const [dayLoading, setDayLoading] = useState({});
  const [savingField, setSavingField] = useState(null); // isOpen | buyIsOpen | sellIsOpen | null

  const [selectedDay, setSelectedDay] = useState(todayDayOfWeek);
  // پیش‌نویس قابل‌ویرایش فقط برای دو فیلد ساعت شروع/پایان در تب ساعات کاری.
  // isOpen/buyIsOpen/sellIsOpen در آن تب ویرایش نمی‌شوند و همیشه مستقیم از
  // dayData خوانده می‌شوند تا هرگز یک نسخه‌ی جدا و قدیمی از آن‌ها نگه‌داری نشود.
  const [hoursDraft, setHoursDraft] = useState({ openingTime: "", closingTime: "" });
  const [hoursSaving, setHoursSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // خواندن تازه‌ی وضعیت یک روز از سرور و جایگزینی کامل مقدار آن در cache.
  // این تابع همیشه پاسخ سرور را عیناً به‌عنوان حقیقت می‌نشاند؛ هیچ مقدار
  // پیش‌فرض یا محلی جایگزین آن نمی‌شود.
  const loadDay = async (day) => {
    try {
      setError("");
      setDayLoading((prev) => ({ ...prev, [day]: true }));

      const response = await getFresh(`/Market/working-hours?dayOfWeek=${day}`);
      if (!isMountedRef.current) return null;

      const data = response.data.data;
      const normalized = {
        isOpen: !!data.isOpen,
        buyIsOpen: !!data.buyIsOpen,
        sellIsOpen: !!data.sellIsOpen,
        openingTime: data.openingTime || "",
        closingTime: data.closingTime || "",
      };

      setDayData((prev) => ({ ...prev, [day]: normalized }));

      if (day === selectedDay) {
        setHoursDraft({
          openingTime: toHHMM(normalized.openingTime),
          closingTime: toHHMM(normalized.closingTime),
        });
      }

      return normalized;
    } catch (err) {
      console.error(err);
      if (!isMountedRef.current) return null;
      setError(extractErrorMessage(err, "خطا در دریافت ساعات کاری بازار"));
      return null;
    } finally {
      if (isMountedRef.current) {
        setDayLoading((prev) => ({ ...prev, [day]: false }));
      }
    }
  };

  const fetchMarkets = async () => {
    try {
      setError("");
      setMarketsLoading(true);
      const response = await getFresh("/Market");
      if (!isMountedRef.current) return;
      setMarkets(response.data.data || []);
    } catch (err) {
      console.error(err);
      if (!isMountedRef.current) return;
      setError(extractErrorMessage(err, "خطا در دریافت بازارها"));
    } finally {
      if (isMountedRef.current) setMarketsLoading(false);
    }
  };

  // بارگذاری اولیه: لیست بازارها + وضعیت امروز (برای کارت وضعیت بازار)
  useEffect(() => {
    fetchMarkets();
    loadDay(todayDayOfWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // هر بار که تب «ساعات کاری» فعال می‌شود یا روز انتخاب‌شده تغییر می‌کند،
  // همیشه یک درخواست تازه به سرور زده می‌شود (حتی اگر همان روز قبلاً کش
  // شده باشد) تا مقدار نمایش‌داده‌شده هرگز با واقعیت سرور فرق نکند.
  useEffect(() => {
    if (viewMode === "hours") {
      loadDay(selectedDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDay]);

  const handleMarketChange = (index, field, value) => {
    const updatedMarkets = [...markets];

    updatedMarkets[index] = {
      ...updatedMarkets[index],
      [field]: value,
    };

    setMarkets(updatedMarkets);
  };

  // مقدار متنی نمایش‌داده‌شده در اینپوت قیمت را برمی‌گرداند.
  // اگر کاربر در حال تایپ روی همین فیلد است، مقدار خام (draft) را نشان می‌دهد
  // تا نشانگر ماوس هنگام ویرایش وسط عدد پرش نکند؛ در غیر این صورت مقدار
  // فرمت‌شده با جداکننده سه‌رقمی نمایش داده می‌شود.
  const getPriceDisplayValue = (market, field) => {
    const key = `${market.asset}-${field}`;
    if (editingField === key && key in draftValues) {
      return draftValues[key];
    }
    return formatWithCommas(market[field]);
  };

  const handlePriceFocus = (market, field) => {
    const key = `${market.asset}-${field}`;
    setEditingField(key);
    setDraftValues((prev) => ({
      ...prev,
      [key]: String(market[field] ?? ""),
    }));
  };

  const handlePriceDraftChange = (index, market, field, rawValue) => {
    const key = `${market.asset}-${field}`;
    setDraftValues((prev) => ({ ...prev, [key]: rawValue }));
    handleMarketChange(index, field, parseCommaNumber(rawValue));
  };

  const handlePriceBlur = (market, field) => {
    const key = `${market.asset}-${field}`;
    setEditingField((prev) => (prev === key ? null : prev));
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateMarket = (market) => {
    if (market.bid === "" || market.bid === null || isNaN(Number(market.bid))) {
      return "قیمت خرید نامعتبر است";
    }
    if (market.ask === "" || market.ask === null || isNaN(Number(market.ask))) {
      return "قیمت فروش نامعتبر است";
    }
    if (Number(market.bid) < 0 || Number(market.ask) < 0) {
      return "قیمت نمی‌تواند منفی باشد";
    }
    if (Number(market.bid) > Number(market.ask)) {
      return "قیمت خرید نمی‌تواند بیشتر از قیمت فروش باشد";
    }
    return null;
  };

  // ذخیره‌ی قیمت یک بازار. کل شیء بازار (که از GET /Market گرفته شده و شامل
  // id/asset/provider/... است) عیناً به POST /Market ارسال می‌شود تا هیچ
  // فیلدی گم نشود، و پس از ذخیره، لیست بازارها دوباره از سرور خوانده می‌شود
  // تا UI دقیقاً با وضعیت واقعی سرور یکسان بماند (نه فقط مقدار محلی).
  const saveMarket = async (market) => {
    const validationMessage = validateMarket(market);
    if (validationMessage) {
      setMessage("");
      setError(`${market.asset}: ${validationMessage}`);
      return;
    }

    try {
      setError("");
      setMessage("");
      setSavingAsset(market.asset);

      await axios.post("/Market", market);
      if (!isMountedRef.current) return;

      await fetchMarkets();
      if (!isMountedRef.current) return;

      setMessage(`بازار ${market.asset} با موفقیت ذخیره شد`);
    } catch (err) {
      console.error(err);
      if (!isMountedRef.current) return;
      setError(extractErrorMessage(err, `خطا در ذخیره بازار ${market.asset}`));
    } finally {
      if (isMountedRef.current) setSavingAsset(null);
    }
  };

  // ذخیره‌ی ساعت شروع/پایان روز انتخاب‌شده در تب «ساعات کاری بازار».
  // PUT /Market/working-hours یک DTO کامل می‌خواهد، پس isOpen/buyIsOpen/
  // sellIsOpen همان روز (از dayData، یعنی آخرین مقدار تأییدشده‌ی سرور) هم
  // در بدنه درخواست ارسال می‌شود تا این ذخیره وضعیت باز/بسته را بازنویسی
  // نکند. پس از موفقیت، همان روز دوباره از سرور خوانده می‌شود.
  const saveWorkingHours = async (e) => {
    e.preventDefault();

    if (!hoursDraft.openingTime || !hoursDraft.closingTime) {
      setMessage("");
      setError("ساعت شروع و پایان را وارد کنید");
      return;
    }
    if (hoursDraft.openingTime >= hoursDraft.closingTime) {
      setMessage("");
      setError("ساعت پایان باید بعد از ساعت شروع باشد");
      return;
    }

    const current = dayData[selectedDay];
    if (!current) {
      setError("اطلاعات این روز هنوز از سرور دریافت نشده است");
      return;
    }

    try {
      setError("");
      setMessage("");
      setHoursSaving(true);

      await axios.put("/Market/working-hours", {
        dayOfWeek: selectedDay,
        isOpen: current.isOpen,
        buyIsOpen: current.buyIsOpen,
        sellIsOpen: current.sellIsOpen,
        openingTime: hoursDraft.openingTime,
        closingTime: hoursDraft.closingTime,
      });

      if (!isMountedRef.current) return;

      const confirmed = await loadDay(selectedDay);
      if (!isMountedRef.current) return;

      if (confirmed) {
        setMessage("ساعات کاری با موفقیت ذخیره شد");
      }
    } catch (err) {
      console.error(err);
      if (!isMountedRef.current) return;
      setError(extractErrorMessage(err, "خطا در ذخیره ساعات کاری"));
    } finally {
      if (isMountedRef.current) setHoursSaving(false);
    }
  };

  // تغییر isOpen / buyIsOpen / sellIsOpen برای امروز (دکمه‌های کارت وضعیت
  // در تب «مدیریت بازارها»). هیچ به‌روزرسانی خوش‌بینانه‌ی محلی انجام
  // نمی‌شود — دکمه‌ها تا زمان تأیید سرور غیرفعال می‌مانند و مقدار نمایشی
  // فقط پس از یک GET موفق (loadDay) که پاسخ واقعی سرور را برمی‌گرداند
  // تغییر می‌کند. openingTime/closingTime فعلی امروز هم در بدنه درخواست
  // ارسال می‌شود تا این تغییر سریع، ساعات کاری ذخیره‌شده را پاک نکند.
  const toggleTodayField = async (field) => {
    const current = dayData[todayDayOfWeek];
    if (!current || savingField) return;

    try {
      setError("");
      setMessage("");
      setSavingField(field);

      await axios.put("/Market/working-hours", {
        dayOfWeek: todayDayOfWeek,
        isOpen: current.isOpen,
        buyIsOpen: current.buyIsOpen,
        sellIsOpen: current.sellIsOpen,
        openingTime: current.openingTime,
        closingTime: current.closingTime,
        [field]: !current[field],
      });

      if (!isMountedRef.current) return;

      const confirmed = await loadDay(todayDayOfWeek);
      if (!isMountedRef.current) return;

      if (confirmed) {
        setMessage("وضعیت بازار با موفقیت به‌روزرسانی شد");
      }
    } catch (err) {
      console.error(err);
      if (!isMountedRef.current) return;
      setError(extractErrorMessage(err, "خطا در تغییر وضعیت بازار"));
    } finally {
      if (isMountedRef.current) setSavingField(null);
    }
  };

  const todayStatus = dayData[todayDayOfWeek];
  const todayStatusLoading = !!dayLoading[todayDayOfWeek];
  const selectedDayLoading = !!dayLoading[selectedDay];

  return (
    <div className="gu-root" dir="rtl">
      <div className="gu-wrap">
        <button className="gu-back" onClick={() => navigate("/dashboard")}>
          → بازگشت به داشبورد
        </button>

        <div className="gu-header">
          <div className="gu-eyebrow">مدیریت بازارها</div>
          <h2 className="gu-title">قیمت‌گذاری و ساعات کاری بازارها</h2>
        </div>

        <div className="gu-tabs" role="tablist">
          <div className="gu-tab">
            <input
              type="radio"
              id="gm-tab-markets"
              name="gm-viewmode"
              value="markets"
              checked={viewMode === "markets"}
              onChange={() => setViewMode("markets")}
            />
            <label htmlFor="gm-tab-markets">مدیریت بازارها</label>
          </div>
          <div className="gu-tab">
            <input
              type="radio"
              id="gm-tab-hours"
              name="gm-viewmode"
              value="hours"
              checked={viewMode === "hours"}
              onChange={() => setViewMode("hours")}
            />
            <label htmlFor="gm-tab-hours">ساعات کاری بازار</label>
          </div>
        </div>

        {message && (
          <p className="gu-success" role="status">
            {message}
          </p>
        )}
        {error && (
          <p className="gu-error" role="alert">
            {error}
          </p>
        )}

        {/* -------------------- Markets -------------------- */}

        {viewMode === "markets" && (
          <div className="gu-panel">
            {/* وضعیت الان بازار */}
            <div className="gm-status-card">
              <div className="gm-status-header">
                <span className="gm-status-title">
                  وضعیت بازار امروز — {DAY_LABELS_FA[todayDayOfWeek]}
                </span>
                {todayStatusLoading && (
                  <span className="gm-status-loading">در حال دریافت...</span>
                )}
              </div>

              {todayStatus && (
                <>
                  <div className="gm-status-hours">
                    ساعت کاری روتین امروز:{" "}
                    <span dir="ltr">
                      {toHHMM(todayStatus.openingTime) || "--:--"} تا{" "}
                      {toHHMM(todayStatus.closingTime) || "--:--"}
                    </span>
                  </div>

                  <div className="gm-status-toggles">
                    <button
                      type="button"
                      className={`gm-toggle-btn gm-toggle-lg ${
                        todayStatus.isOpen ? "is-on" : "is-off"
                      }`}
                      onClick={() => toggleTodayField("isOpen")}
                      disabled={!!savingField}
                      aria-pressed={todayStatus.isOpen}
                    >
                      {savingField === "isOpen"
                        ? "..."
                        : `بازار: ${todayStatus.isOpen ? "باز" : "بسته"}`}
                    </button>

                    <button
                      type="button"
                      className={`gm-toggle-btn gm-toggle-lg ${
                        todayStatus.buyIsOpen ? "is-on" : "is-off"
                      }`}
                      onClick={() => toggleTodayField("buyIsOpen")}
                      disabled={!!savingField}
                      aria-pressed={todayStatus.buyIsOpen}
                    >
                      {savingField === "buyIsOpen"
                        ? "..."
                        : `خرید: ${todayStatus.buyIsOpen ? "باز" : "بسته"}`}
                    </button>

                    <button
                      type="button"
                      className={`gm-toggle-btn gm-toggle-lg ${
                        todayStatus.sellIsOpen ? "is-on" : "is-off"
                      }`}
                      onClick={() => toggleTodayField("sellIsOpen")}
                      disabled={!!savingField}
                      aria-pressed={todayStatus.sellIsOpen}
                    >
                      {savingField === "sellIsOpen"
                        ? "..."
                        : `فروش: ${todayStatus.sellIsOpen ? "باز" : "بسته"}`}
                    </button>
                  </div>
                </>
              )}

              {!todayStatus && !todayStatusLoading && (
                <div className="gm-status-hours">
                  وضعیت بازار در دسترس نیست.
                </div>
              )}
            </div>

            <div className="gu-table-card gm-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>دارایی</th>
                    <th>قیمت خرید (Bid)</th>
                    <th>قیمت فروش (Ask)</th>
                    <th>فعال</th>
                    <th>خرید فعال</th>
                    <th>فروش فعال</th>
                    <th>عملیات</th>
                  </tr>
                </thead>

                <tbody>
                  {marketsLoading ? (
                    <tr>
                      <td colSpan="7" className="gu-empty">
                        در حال بارگذاری...
                      </td>
                    </tr>
                  ) : markets.length > 0 ? (
                    markets.map((market, index) => (
                      <tr key={market.asset}>
                        <td className="gm-asset-cell">{market.asset}</td>

                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="gm-price-input"
                            value={getPriceDisplayValue(market, "bid")}
                            onFocus={() => handlePriceFocus(market, "bid")}
                            onChange={(e) =>
                              handlePriceDraftChange(
                                index,
                                market,
                                "bid",
                                e.target.value
                              )
                            }
                            onBlur={() => handlePriceBlur(market, "bid")}
                            aria-label={`قیمت خرید ${market.asset}`}
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="gm-price-input"
                            value={getPriceDisplayValue(market, "ask")}
                            onFocus={() => handlePriceFocus(market, "ask")}
                            onChange={(e) =>
                              handlePriceDraftChange(
                                index,
                                market,
                                "ask",
                                e.target.value
                              )
                            }
                            onBlur={() => handlePriceBlur(market, "ask")}
                            aria-label={`قیمت فروش ${market.asset}`}
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={market.isActive}
                            onChange={(e) =>
                              handleMarketChange(
                                index,
                                "isActive",
                                e.target.checked
                              )
                            }
                            aria-label={`فعال بودن بازار ${market.asset}`}
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={market.buyIsActive}
                            onChange={(e) =>
                              handleMarketChange(
                                index,
                                "buyIsActive",
                                e.target.checked
                              )
                            }
                            aria-label={`فعال بودن خرید ${market.asset}`}
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={market.sellIsActive}
                            onChange={(e) =>
                              handleMarketChange(
                                index,
                                "sellIsActive",
                                e.target.checked
                              )
                            }
                            aria-label={`فعال بودن فروش ${market.asset}`}
                          />
                        </td>

                        <td>
                          <button
                            type="button"
                            className="gm-save-btn"
                            onClick={() => saveMarket(market)}
                            disabled={savingAsset === market.asset}
                          >
                            {savingAsset === market.asset ? "..." : "ذخیره"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="gu-empty">
                        بازاری یافت نشد
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------- Working Hours -------------------- */}

        {viewMode === "hours" && (
          <div className="gu-panel">
            <div className="gu-form-card">
              <form onSubmit={saveWorkingHours}>
                <div className="gu-field">
                  <label htmlFor="gm-day-select">روز هفته</label>
                  <select
                    id="gm-day-select"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                  >
                    {DAY_NAMES_EN.map((day) => (
                      <option key={day} value={day}>
                        {DAY_LABELS_FA[day]}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedDayLoading ? (
                  <div className="gu-loading">در حال دریافت ساعات کاری...</div>
                ) : (
                  <>
                    <div className="gu-field">
                      <label htmlFor="gm-open-time">ساعت شروع</label>
                      <input
                        id="gm-open-time"
                        type="time"
                        value={hoursDraft.openingTime}
                        onChange={(e) =>
                          setHoursDraft({
                            ...hoursDraft,
                            openingTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="gu-field">
                      <label htmlFor="gm-close-time">ساعت پایان</label>
                      <input
                        id="gm-close-time"
                        type="time"
                        value={hoursDraft.closingTime}
                        onChange={(e) =>
                          setHoursDraft({
                            ...hoursDraft,
                            closingTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <button
                      type="submit"
                      className="gu-submit"
                      disabled={hoursSaving || !dayData[selectedDay]}
                    >
                      {hoursSaving ? "در حال ذخیره..." : "ذخیره ساعات کاری"}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Markets;